import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import { SEND_DEB_FORM, UPDATE_INSTALLER_CONSENT_MUTATION } from 'Queries/Mutations';
import { createPatchEinwilligungByInstallerIdData } from 'Queries/QueriesHelper';
import { FormikProvider, useFormik } from 'formik';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';
import { useInstallerStatusSummary } from 'hooks/UseInstallerStatusSummary';
import { useFormUtilityData } from 'hooks/useFormUtilityData';
import PropTypes from 'prop-types';

import { shouldFillDebTestData } from 'components/DEBForm/Utils/DebTestSetup';
import { DataPolicyContent } from 'components/DataPolicy/DataPolicyContentExpanded';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormPageWrapper } from 'components/FormHelper/FormPageWrapper';
import { FormSection } from 'components/FormHelper/FormSection';
import { InputField } from 'components/FormHelper/InputField';
import { InputRow } from 'components/FormHelper/InputRow';
import { SubmitPopup } from 'components/FormHelper/SubmitPopup';
import { UploadField } from 'components/FormHelper/UploadField/UploadField';
import { HintText } from 'components/HintText/HintText';
import { HINTTEXT_TYPE } from 'components/HintText/HintTextType';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { PageTitle } from 'components/PageTitle/PageTitle';

import { DOCUMENT_NAME_TO_ID } from 'global/SharedDocumentFields';
import {
  DEB_ACTIVE_PAGE,
  FORM_CONSTANTS,
  INSTALLER_ENTRIES,
  UPLOAD_RESTRICTION_ERROR_MESSAGE,
} from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';

import { FFormDebug } from 'utils/FFormDebug';
import { calculateTotalFilesSize, getFileWithNames } from 'utils/FileUtil';
import { checkKeyDown, fillFormUploadIVDEB } from 'utils/FormUtils';
import { isRegisteredInstallerOnlyHilfsbetrieb } from 'utils/InstallerUtils';
import { getMyDataPath } from 'utils/UrlUtil';
import { useQualificationBasisData } from 'utils/qualificationBasisData';
import { getTenantId } from 'utils/tenantUtil';

import { useDebFormSecondPageLogic } from './Hooks/useDebFormSecondPageLogic';
import { prepDebDataForBackend } from './Utils/DebFormDataUtil';
import { createEmailSubject, shouldDebSecondPageSectionRender } from './Utils/DebFormSectionUtil';
import { DEB_DOCUMENTS_FIELDS, DEB_EXPERTS_DOCUMENTS_FIELDS } from './Utils/DocumentsFields';

import './DebForm.sass';

export const DebFormSecondPage = ({
  valuesFirstPage,
  initialValuesFirstPage,
  installer,
  setPage,
  dataPolicyDocuments,
  selfDeclarationCompanyFacilityLink,
  installerContractLink,
  expertQualificationCertificateGasLink,
  expertQualificationCertificateEnergyLink,
  trainingCertificateExtensionLink,
}) => {
  const { refetchInstaller } = useCurrentInstaller();
  const installerStatusSummary = useInstallerStatusSummary(installer);
  const { initialValues, validationSchema } = useDebFormSecondPageLogic(
    valuesFirstPage,
    initialValuesFirstPage,
    installerStatusSummary,
    installer
  );

  const formik = useFormik({
    initialValues: shouldFillDebTestData ? fillFormUploadIVDEB(initialValues) : initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
  });

  const { values, errors, touched, handleSubmit } = formik;
  const [uploadRestrictionError, setUploadRestrictionError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [response, setResponse] = useState('');
  const [openPopup, setOpenPopup] = useState(false);

  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);
  const [sendDebForm] = useMutation(SEND_DEB_FORM);
  const [updateInstallerConsentMutation] = useMutation(UPDATE_INSTALLER_CONSENT_MUTATION);
  const { comboBoxOptions } = useFormUtilityData();
  const qualificationBasisData = useQualificationBasisData();
  const onSubmit = async () => {
    setIsSubmitting(true);
    const subject = createEmailSubject(values, installerStatusSummary);
    const files = getFileWithNames(values, valuesFirstPage, installer);
    let formData;
    try {
      formData = prepDebDataForBackend(
        initialValuesFirstPage,
        valuesFirstPage,
        values,
        subject,
        files,
        installerStatusSummary,
        comboBoxOptions,
        qualificationBasisData
      );
      const patchData = createPatchEinwilligungByInstallerIdData(
        values.isAddressPublic,
        values.canSendMail,
        values.informationOperators,
        values.addressOperators
      );
      if (
        patchData.input.zuAdressveroeff !== installer.zuAdressveroeff ||
        patchData.input.zuInfoversand !== installer.zuInfoversand ||
        JSON.stringify(patchData.input.informationOperators) !==
          JSON.stringify(installer.informationOperators || []) ||
        JSON.stringify(patchData.input.addressOperators) !==
          JSON.stringify(installer.addressOperators || [])
      ) {
        const consentResponse = await updateInstallerConsentMutation({
          variables: patchData,
        });
        if (consentResponse.error || consentResponse.data?.updateInstallerConsent?.status !== 200) {
          throw new Error(
            consentResponse.error?.message ||
              `Consent mutation failed with status: ${consentResponse.data?.updateInstallerConsent?.status}`
          );
        }
      }
    } catch (e) {
      console.error(e);
      setOpenPopup(true);
      setResponse('ERROR');
      setIsSubmitting(false);
      return;
    }
    sendDebForm({
      variables: {
        debInput: formData,
      },
    })
      .then(({ data }) => {
        setOpenPopup(true);
        setResponse(data.deb.status); // this can't be a constant as graphql sometimes goes to 200 even with errors
      })
      .catch(() => {
        setOpenPopup(true);
        setResponse('ERROR');
      })
      .finally(() => {
        setIsSubmitting(false);
        refetchInstaller();
      });
  };

  useEffect(() => {
    // total file size calculation and validation
    const totalSize = calculateTotalFilesSize(values);
    if (totalSize > FORM_CONSTANTS.MAX_UPLOAD_TOTAL_FILE_SIZE) {
      setUploadRestrictionError(UPLOAD_RESTRICTION_ERROR_MESSAGE.TOTAL_FILE_SIZE);
    } else {
      setUploadRestrictionError('');
    }

    formik.validateForm();
  }, [values]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getSectionLink = (section) => {
    // check documentFields.jsx and HWPORTAL-3692 to get more info on the sections. Match text on the story with infoText on the fields
    switch (section.id) {
      case DOCUMENT_NAME_TO_ID.businessEquipmentConfirmation:
        return selfDeclarationCompanyFacilityLink;
      case DOCUMENT_NAME_TO_ID.installerContract:
        return installerContractLink;
      case DOCUMENT_NAME_TO_ID.installerContractGas:
        return installerContractLink;
      case DOCUMENT_NAME_TO_ID.installerContractGasResponsible:
        return installerContractLink;
      case DOCUMENT_NAME_TO_ID.qualificationProofElectricity:
        return expertQualificationCertificateEnergyLink;
      case DOCUMENT_NAME_TO_ID.qualificationProofGas:
        return expertQualificationCertificateGasLink;
      case DOCUMENT_NAME_TO_ID.trainingCertificateExtension:
        return trainingCertificateExtensionLink;
      default:
        return null;
    }
  };
  const renderedFields = useRef(0);
  const evaluateConditionsConfig = {
    triggers: formik.values.documentsTrigger,
    isOnlyHilfsbetrieb: isRegisteredInstallerOnlyHilfsbetrieb(installerStatusSummary),
    form: FORM_CONSTANTS.DEB,
  };

  return (
    <FormikProvider value={formik}>
      <form onKeyDown={checkKeyDown} onSubmit={handleSubmit} className="deb-form__second-page">
        <FormPageWrapper>
          <div className="title-section">
            <PageTitle text="Dokumentenupload und weitere Angaben" />
            <div>
              Bitte laden Sie die geforderten Dokumente hoch. Aufgrund Ihrer Auswahl auf der
              vorherigen Seite benötigen wir folgende Dokumente. Am Ende überprüfen Sie bitte Ihre
              Datenschutzeinstellung und senden Sie uns nach Bedarf Ihre Fragen.
            </div>
            <br />
            {renderedFields.current > 0 ? (
              <HintText>
                Folgende Formate werden beim Upload unterstützt: PDF, JPG, JPEG, GIF, PNG
              </HintText>
            ) : null}
          </div>
          {DEB_DOCUMENTS_FIELDS[installerStatusSummary.installerEntry][getTenantId()].map(
            (section) => {
              if (
                shouldDebSecondPageSectionRender(
                  section,
                  formik.values.documentsTrigger,
                  installerStatusSummary
                ) &&
                (section.whenShouldFieldRender
                  ? evaluateCondition(section.whenShouldFieldRender, evaluateConditionsConfig)
                  : true)
              ) {
                renderedFields.current += 1;
                let { infoText } = section;
                const infoLink = getSectionLink(section);

                if (section.id === DOCUMENT_NAME_TO_ID.gridOperatorRegistration) {
                  if (
                    installerStatusSummary.installerEntry ===
                      INSTALLER_ENTRIES.MAIN_ENERGY_GUEST_GAS &&
                    !formik.values.documentsTrigger.every((id) => [1, 2, 3, 11].includes(id))
                  ) {
                    infoText = '';
                  } else if (
                    installerStatusSummary.installerEntry ===
                      INSTALLER_ENTRIES.GUEST_ENERGY_MAIN_GAS &&
                    !formik.values.documentsTrigger.every((id) => [1, 2, 3, 12].includes(id))
                  ) {
                    infoText = '';
                  }
                }
                return (
                  <FormSection
                    key={section.id}
                    subtitle={`·  ${section.title} ${
                      evaluateCondition(section.isOptional, evaluateConditionsConfig) ? '' : '*'
                    }`}
                    tooltip={section.tooltip}
                    navigationTooltip={section.navigationTooltip}>
                    <InputRow>
                      <UploadField
                        infoText={infoText}
                        key={section.name}
                        name={section.name}
                        optional={evaluateCondition(section.isOptional, {
                          isOnlyHilfsbetrieb:
                            isRegisteredInstallerOnlyHilfsbetrieb(installerStatusSummary),
                          form: FORM_CONSTANTS.DEB,
                        })}
                        files={values[section.name]}
                        formik={formik}
                        infoLink={infoLink}
                      />
                    </InputRow>
                  </FormSection>
                );
              }
              return null;
            }
          )}

          {Object.keys(values.expertTrigger).map((key) => {
            const fields = [];
            const expert = valuesFirstPage.experts?.find(
              (expertFilter) => Number(expertFilter.id) === Number(key)
            );
            DEB_EXPERTS_DOCUMENTS_FIELDS[installerStatusSummary.installerEntry][
              getTenantId()
            ].forEach((section) => {
              const infoLink = getSectionLink(section);

              if (!expert) return;
              if (
                shouldDebSecondPageSectionRender(
                  section,
                  values.expertTrigger[key],
                  installerStatusSummary,
                  expert
                )
              ) {
                renderedFields.current += 1;
                fields.push(
                  <FormSection
                    key={section.id}
                    subtitle={`·  ${section.title} ${
                      evaluateCondition(section.isOptional, evaluateConditionsConfig) ? '' : '*'
                    }`}
                    tooltip={section.tooltip}
                    navigationTooltip={section.navigationTooltip}>
                    <InputRow>
                      <UploadField
                        infoText={section.infoText}
                        key={`${key}.${section.name}`}
                        name={`${key}.${section.name}`}
                        files={values[key]?.[section.name] || []}
                        onChange={formik.handleChange}
                        formik={formik}
                        error={Boolean(formik.errors[section.name])}
                        helperText={formik.errors[section.name] || ''}
                        infoLink={infoLink}
                      />
                    </InputRow>
                  </FormSection>
                );
              }
            });
            if (fields.length > 0) {
              if (expert)
                return (
                  <FormSection title={`${expert.lastName}  ${expert.firstName}`} key={expert.id}>
                    {fields}
                  </FormSection>
                );
            }
            return null;
          })}
          <FormSection key="changes" title="Weitere Angaben">
            <div>
              Sie haben noch Fragen oder weitere Anmerkungen? Dann können Sie uns hier eine
              Nachrichten hinterlassen.
            </div>
            <InputField
              key="changes"
              name="changes"
              fullWidth
              label="Freitext"
              oldValue=""
              variant="standard"
              value={values.changes}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.changes)}
              helperText={formik.errors.changes || ''}
              multiline
              rows={6}
              rowsMax={6}
            />
          </FormSection>
          <FormSection key="changesAndPrivacy" title="Datenschutz">
            <div>Hier können Sie ihre Zustimmungen bearbeiten</div>
            <div className="agreement-section__wrapper">
              <DataPolicyContent
                dsgvoAddressPublic={values.isAddressPublic}
                dsgvoCanSendMail={values.canSendMail}
                dsgvoAddressPublicName="isAddressPublic"
                dsgvoCanSendMailName="canSendMail"
                onChangeDsvgoAddressPublic={formik.handleChange}
                onChangeDsvgoCanSendMail={formik.handleChange}
                documents={dataPolicyDocuments}
                showExpandedConsentSections
                currentBusinessUnit={values.pflegendeBU}
                informationOperators={values.informationOperators}
                addressOperators={values.addressOperators}
                onOperatorSelectionsChange={({ informationOperators, addressOperators }) => {
                  formik.setFieldValue('informationOperators', informationOperators);
                  formik.setFieldValue('addressOperators', addressOperators);
                }}
              />
            </div>
          </FormSection>
        </FormPageWrapper>

        {uploadRestrictionError && (
          <div className="deb-form__upload-restriction-error">
            <HintText type={HINTTEXT_TYPE.ERROR}>{uploadRestrictionError}</HintText>
          </div>
        )}
        {isSubmitting ? (
          <LoadingIndicator />
        ) : (
          <FormActionButtons
            errorMessage="Bitte füllen Sie alle mit * gekennzeichneten Pflichtfelder sowie Uploadfelder."
            hasErrors={hasErrors || Boolean(uploadRestrictionError)}
            titleLeft="Zurück"
            titleRight="Absenden"
            onClickLeft={() => {
              setPage(DEB_ACTIVE_PAGE.FORM_PAGE);
            }}
            onClickRight={() => {
              onSubmit();
            }}
          />
        )}

        <FFormDebug values={values} errors={errors} touched={touched} />
      </form>
      <SubmitPopup
        type="EDIT_COMPANY"
        loading={isSubmitting}
        popupOpen={openPopup}
        setPopupOpen={() => setOpenPopup(false)}
        response={response}
        redirectLinkOnSuccessOrCancel={getMyDataPath()}
        onErrorRetry={() => {
          onSubmit();
        }}
      />
    </FormikProvider>
  );
};

DebFormSecondPage.displayName = 'DebFormSecondPage';
DebFormSecondPage.propTypes = {
  valuesFirstPage: PropTypes.object.isRequired,
  initialValuesFirstPage: PropTypes.object.isRequired,
  installer: PropTypes.object.isRequired,
  setPage: PropTypes.func.isRequired,
  expertQualificationCertificateGasLink: PropTypes.string,
  expertQualificationCertificateEnergyLink: PropTypes.string,
  installerContractLink: PropTypes.string,
  selfDeclarationCompanyFacilityLink: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  dataPolicyDocuments: PropTypes.array.isRequired,
  trainingCertificateExtensionLink: PropTypes.string,
};

DebFormSecondPage.defaultProps = {
  expertQualificationCertificateGasLink: undefined,
  expertQualificationCertificateEnergyLink: undefined,
  installerContractLink: undefined,
  selfDeclarationCompanyFacilityLink: undefined,
  trainingCertificateExtensionLink: undefined,
};

DebFormSecondPage.defaultProps = {};
